/**
 * AnalyticsSectionCard — themed shell for one Home analytics section.
 *
 * Owns the title/icon header and the loading / error states so each section's
 * body component stays focused on rendering its data.
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import Card from '../../../../Presentation/components/ui/Card';
import {
  AppCardTitle,
  AppSmallText
} from '../../../../Presentation/components/ui/Typography';

interface AnalyticsSectionCardProps {
  title: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  className?: string;
  children: React.ReactNode;
}

const SectionSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-3" aria-hidden>
    {[...Array(4)].map((_, index) => (
      <div key={index} className="space-y-2">
        <div className="h-3 bg-gray-200 rounded w-1/3" />
        <div className="h-2 bg-gray-100 rounded w-full" />
      </div>
    ))}
  </div>
);

const AnalyticsSectionCard: React.FC<AnalyticsSectionCardProps> = ({
  title,
  icon,
  isLoading = false,
  isError = false,
  className = '',
  children
}) => {
  const {t} = useTranslation();

  return (
    <Card className={`p-5 flex flex-col gap-4 ${className}`}>
      <div className="flex items-center gap-3">
        <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-50 text-primary flex items-center justify-center">
          {icon}
        </span>
        <AppCardTitle className="font-semibold text-grayText">
          {title}
        </AppCardTitle>
      </div>

      {isLoading ? (
        <SectionSkeleton />
      ) : isError ? (
        <AppSmallText className="text-gray-400 py-6 text-center">
          {t('analytics.load_error')}
        </AppSmallText>
      ) : (
        children
      )}
    </Card>
  );
};

export default AnalyticsSectionCard;
